import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  Collapse,
  Grid,
  CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';
import SettingsIcon from '@mui/icons-material/Settings';
import LaunchIcon from '@mui/icons-material/Launch';
import trackingService from '../../services/trackingService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

interface EventEmbedGeneratorProps {
  eventId: string;
  publicLinkId: string | undefined;
  organizationId: string;
  eventTitle: string;
}

const EventEmbedGenerator: React.FC<EventEmbedGeneratorProps> = ({
  eventId,
  publicLinkId,
  organizationId,
  eventTitle
}) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedPublicLinkId, setGeneratedPublicLinkId] = useState<string | undefined>(publicLinkId);
  const [error, setError] = useState<string | null>(null);
  const [customOptions, setCustomOptions] = useState({
    width: '100%',
    height: '600px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    includeTracking: true
  });

  // Generate a publicLinkId for the event if it doesn't have one
  const generatePublicLinkId = async () => {
    if (generatedPublicLinkId) return; // Already has a public link ID
    
    try {
      setIsGeneratingLink(true);
      setError(null);
      
      // Create a unique ID for the public link
      const newPublicLinkId = `${eventId}-${Date.now()}`;
      
      // Update the event in Firestore
      const eventRef = doc(db, 'events', eventId);
      await updateDoc(eventRef, {
        publicLinkId: newPublicLinkId,
        isPublic: true // Ensure it's marked as public
      });
      
      // Update local state
      setGeneratedPublicLinkId(newPublicLinkId);
      
    } catch (err) {
      console.error('Error generating public link:', err);
      setError('Failed to generate public link. Please try again.');
    } finally {
      setIsGeneratingLink(false);
    }
  };
  
  // Effect to auto-generate public link if needed
  useEffect(() => {
    if (expanded && !generatedPublicLinkId && eventId) {
      generatePublicLinkId();
    }
  }, [expanded, generatedPublicLinkId, eventId]);
  
  // Generate a trackable URL for this embed
  const generateEmbedCode = () => {
    if (!generatedPublicLinkId) {
      if (isGeneratingLink) {
        return '<p>Generating public link for this event...</p>';
      }
      return '<p>Preparing embed code...</p>';
    }

    const baseUrl = `${window.location.origin}/events/embed/${generatedPublicLinkId}`;
    
    let embedUrl = baseUrl;
    
    // Add tracking if enabled
    if (customOptions.includeTracking) {
      try {
        // Generate a tracking ID using the tracking service
        const { trackingId, shareUrl } = trackingService.generateTrackableUrl(
          baseUrl,
          eventId,
          'event',
          'embed' as any // Using embed as platform type
        );
        
        // Record this as a potential share
        trackingService.recordShare(
          eventId,
          'event',
          'embed' as any,
          shareUrl,
          trackingId,
          organizationId,
          eventId
        ).catch(err => console.error('Error recording embed share:', err));
        
        // Use the trackable URL
        embedUrl = shareUrl;
      } catch (err) {
        console.error('Error generating tracking URL:', err);
        // Fallback to non-tracked URL
        embedUrl = baseUrl;
      }
    }
    
    // Generate the iframe code
    return `<iframe 
  src="${embedUrl}"
  title="${eventTitle.replace(/"/g, '&quot;')}" 
  width="${customOptions.width}" 
  height="${customOptions.height}" 
  style="border: ${customOptions.border}; border-radius: ${customOptions.borderRadius}; overflow: hidden;"
  allow="payment"
  loading="lazy">
</iframe>`;
  };

  const embedCode = generateEmbedCode();

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setCustomOptions(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePreview = () => {
    if (!generatedPublicLinkId) return;
    
    const baseUrl = `${window.location.origin}/events/embed/${generatedPublicLinkId}`;
    window.open(baseUrl, '_blank');
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Embed This Event</Typography>
        </Box>
        <IconButton size="small">
          {expanded ? (
            <SettingsIcon fontSize="small" />
          ) : (
            <LaunchIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add this event to your website by copying and pasting the HTML code below.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isGeneratingLink ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Generating public link...
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={embedCode}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  sx: { 
                    fontFamily: 'monospace', 
                    fontSize: '0.8rem'
                  }
                }}
                sx={{ mb: 2, mt: 1 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopy}
                  disabled={!generatedPublicLinkId}
                >
                  Copy Code
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  onClick={handlePreview}
                  disabled={!generatedPublicLinkId}
                >
                  Preview
                </Button>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  startIcon={<SettingsIcon />}
                >
                  {showAdvanced ? 'Hide' : 'Show'} Advanced Options
                </Button>
              </Box>
              
              {showAdvanced && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Customize Embed
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Width"
                        name="width"
                        value={customOptions.width}
                        onChange={handleOptionChange}
                        size="small"
                        helperText="e.g., 100%, 600px"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Height"
                        name="height"
                        value={customOptions.height}
                        onChange={handleOptionChange}
                        size="small"
                        helperText="e.g., 600px, 800px"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Border"
                        name="border"
                        value={customOptions.border}
                        onChange={handleOptionChange}
                        size="small"
                        helperText="e.g., 1px solid #ddd, none"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Border Radius"
                        name="borderRadius"
                        value={customOptions.borderRadius}
                        onChange={handleOptionChange}
                        size="small"
                        helperText="e.g., 8px, 0"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={customOptions.includeTracking}
                            onChange={handleOptionChange}
                            name="includeTracking"
                          />
                        }
                        label="Enable tracking (recommended for analytics)"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
      
      <Snackbar 
        open={copied} 
        autoHideDuration={3000} 
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          Embed code copied to clipboard!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EventEmbedGenerator;
